import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { themeable } from '@instructure/ui-themeable';
import { testable } from '@instructure/ui-testable';
import { View } from '@instructure/ui-view';
import { Spinner } from '@instructure/ui-spinner';
import getOptionId from '../utils/getOptionId';
var styles = {
  componentId: 'eZsEe',
  template: function template(theme) {
    return "\n\n.eZsEe_dKpE{box-sizing:border-box;list-style-type:none;margin:0;overflow:auto;padding:0}\n\n.eZsEe_ecYO{-ms-user-select:none;-webkit-user-select:none;align-items:center;background:".concat(theme.background || 'inherit', ";cursor:pointer;display:flex;font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.mediumFontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";outline:none;padding:").concat(theme.padding || 'inherit', ";transition:background 0.2s;user-select:none}\n\n.eZsEe_ecYO.eZsEe_doqw{font-size:").concat(theme.smallFontSize || 'inherit', "}\n\n.eZsEe_ecYO.eZsEe_ycrn{font-size:").concat(theme.mediumFontSize || 'inherit', "}\n\n.eZsEe_ecYO.eZsEe_cMDj{font-size:").concat(theme.largeFontSize || 'inherit', "}\n\n.eZsEe_ecYO.eZsEe_cNcI:not(.eZsEe_fckf){background:").concat(theme.highlightedBackground || 'inherit', "}\n\n.eZsEe_ecYO.eZsEe_fGhm:not(.eZsEe_fckf){background:").concat(theme.activeBackground || 'inherit', "}\n\n.eZsEe_ecYO.eZsEe_ywdX:not(.eZsEe_fckf){cursor:not-allowed;opacity:0.5;pointer-events:none}\n\n.eZsEe_ecYO.eZsEe_fckf{cursor:not-allowed;font-weight:").concat(theme.groupFontWeight || 'inherit', ";pointer-events:none}\n\n.eZsEe_ecYO.eZsEe_dXHE{-webkit-padding-start:").concat(theme.groupPadding || 'inherit', ";padding-inline-start:").concat(theme.groupPadding || 'inherit', "}\n\n[dir=ltr] .eZsEe_ecYO.eZsEe_dXHE{padding-left:").concat(theme.groupPadding || 'inherit', "}\n\n[dir=rtl] .eZsEe_ecYO.eZsEe_dXHE{padding-right:").concat(theme.groupPadding || 'inherit', "}\n\n.eZsEe_blJt{color:").concat(theme.labelColor || 'inherit', "}\n\n.eZsEe_cNcI:not(.eZsEe_fckf) .eZsEe_blJt,.eZsEe_fGhm:not(.eZsEe_fckf) .eZsEe_blJt{color:").concat(theme.activeLabelColor || 'inherit', "}\n\n.eZsEe_dnnz{-webkit-margin-end:").concat(theme.iconPaddingEnd || 'inherit', ";-webkit-margin-start:0;margin-inline-end:").concat(theme.iconPaddingEnd || 'inherit', ";margin-inline-start:0}\n\n[dir=ltr] .eZsEe_dnnz{margin-left:0;margin-right:").concat(theme.iconPaddingEnd || 'inherit', "}\n\n[dir=rtl] .eZsEe_dnnz{margin-left:").concat(theme.iconPaddingEnd || 'inherit', ";margin-right:0}\n\n.eZsEe_crZr{z-index:").concat(theme.zIndex || 'inherit', "}");
  },
  'optionsList': 'eZsEe_dKpE',
  'option': 'eZsEe_ecYO',
  'small': 'eZsEe_doqw',
  'medium': 'eZsEe_ycrn',
  'large': 'eZsEe_cMDj',
  'highlighted': 'eZsEe_cNcI',
  'groupLabel': 'eZsEe_fckf',
  'selected': 'eZsEe_fGhm',
  'disabled': 'eZsEe_ywdX',
  'groupItem': 'eZsEe_dXHE',
  'label': 'eZsEe_blJt',
  'icon': 'eZsEe_dnnz',
  'container': 'eZsEe_crZr'
};
import theme from './theme';
/**
---
parent: DeprecatedSelect
---
**/

var SelectOptionsList = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(SelectOptionsList, _Component);

  var _super = _createSuper(SelectOptionsList);

  function SelectOptionsList() {
    var _this;

    _classCallCheck(this, SelectOptionsList);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.optionHeight = 5;

    _this.handleClick = function (event, option) {
      if (option.disabled || option.groupLabel) {
        event.preventDefault();
        return;
      }

      _this.props.onSelect(event, option);
    };

    return _this;
  }

  _createClass(SelectOptionsList, [{
    key: "renderStaticOption",
    value: function renderStaticOption(message) {
      var _this$props = this.props,
          size = _this$props.size,
          onStaticClick = _this$props.onStaticClick;
      return /*#__PURE__*/React.createElement("li", {
        // eslint-disable-line
        tabIndex: "-1",
        role: "option",
        "aria-disabled": "true",
        "aria-selected": "false",
        className: classnames(styles.option, _defineProperty({}, styles[size], size)),
        onClick: onStaticClick
      }, /*#__PURE__*/React.createElement("span", {
        className: styles.label
      }, message));
    }
  }, {
    key: "renderIcon",
    value: function renderIcon(icon) {
      if (typeof icon !== 'undefined' && icon !== null) {
        var Icon = icon;
        return /*#__PURE__*/React.createElement("span", {
          className: styles.icon
        }, /*#__PURE__*/React.createElement(Icon, null));
      }
    }
  }, {
    key: "renderOptions",
    value: function renderOptions() {
      var _this2 = this;

      var _this$props2 = this.props,
          options = _this$props2.options,
          optionsId = _this$props2.optionsId,
          loadingText = _this$props2.loadingText,
          emptyOption = _this$props2.emptyOption,
          selectedOption = _this$props2.selectedOption,
          onHighlightOption = _this$props2.onHighlightOption,
          size = _this$props2.size,
          highlightedIndex = _this$props2.highlightedIndex;

      if (loadingText) {
        return this.renderStaticOption( /*#__PURE__*/React.createElement(Spinner, {
          size: "x-small",
          renderTitle: loadingText
        }));
      }

      if (options.length === 0) {
        return this.renderStaticOption(emptyOption);
      }

      return options.map(function (option, index) {
        var _classnames2;

        var children = option.children,
            id = option.id,
            icon = option.icon,
            groupLabel = option.groupLabel,
            groupItem = option.groupItem;
        var selected = getOptionId(selectedOption) === id;
        var disabled = option.disabled || groupLabel;
        var handlers = {
          onMouseEnter: function onMouseEnter() {
            return onHighlightOption(index);
          },
          onClick: function onClick(event) {
            return _this2.handleClick(event, option);
          }
        };
        /* eslint-disable jsx-a11y/role-has-required-aria-props */

        return /*#__PURE__*/React.createElement("li", Object.assign({}, handlers, {
          role: "option",
          key: id,
          id: "".concat(optionsId, "_").concat(id),
          className: classnames(styles.option, (_classnames2 = {}, _defineProperty(_classnames2, styles.selected, selected), _defineProperty(_classnames2, styles[size], size), _defineProperty(_classnames2, styles.highlighted, index === highlightedIndex), _defineProperty(_classnames2, styles.disabled, disabled), _defineProperty(_classnames2, styles.groupLabel, groupLabel), _defineProperty(_classnames2, styles.groupItem, groupItem), _classnames2)),
          tabIndex: "-1",
          "aria-selected": selected ? 'true' : 'false',
          "aria-disabled": disabled ? 'true' : null
        }), /*#__PURE__*/React.createElement("span", {
          className: styles.label
        }, icon && _this2.renderIcon(icon), children));
        /* eslint-enable jsx-a11y/role-has-required-aria-props */
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props3 = this.props,
          optionsId = _this$props3.optionsId,
          menuRef = _this$props3.menuRef,
          visibleOptionsCount = _this$props3.visibleOptionsCount,
          maxWidth = _this$props3.maxWidth,
          props = _objectWithoutProperties(_this$props3, ["optionsId", "menuRef", "visibleOptionsCount", "maxWidth"]);

      return /*#__PURE__*/React.createElement(View, {
        style: props.style // must pass the style prop for positioning
        ,
        className: styles.container,
        maxWidth: maxWidth || 0,
        borderWidth: "small",
        borderRadius: "medium",
        shadow: "resting",
        background: "primary"
      }, !!this.props.expanded && /*#__PURE__*/React.createElement("ul", {
        className: styles.optionsList,
        id: optionsId,
        onKeyDown: this.props.onKeyDown,
        onKeyUp: this.props.onKeyUp,
        onBlur: this.props.onBlur,
        onClick: function onClick(event) {
          // prevent synthetic event from firing on the document
          // when select is in a dialog, this event could inadvertently close the dialog
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        },
        tabIndex: "-1",
        ref: function ref(el) {
          menuRef(el);

          if (el) {
            // store the height of the   options
            _this3.optionHeight = el.querySelector('li').clientHeight;
          }
        },
        role: "listbox",
        style: {
          maxHeight: "".concat(visibleOptionsCount * this.optionHeight, "px")
        }
      }, this.renderOptions()));
    }
  }]);

  SelectOptionsList.displayName = "SelectOptionsList";
  return SelectOptionsList;
}(Component), _class2.propTypes = {
  /**
  * the selected value
  */
  selectedOption: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node,
    disabled: PropTypes.bool,
    icon: PropTypes.func,
    groupLabel: PropTypes.bool
  })]),
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node,
    disabled: PropTypes.bool,
    icon: PropTypes.func,
    groupLabel: PropTypes.bool
  })),

  /**
   * id for options list element
   */
  optionsId: PropTypes.string,

  /**
  * a function that provides a reference to the internal options list element
  */
  menuRef: PropTypes.func,

  /**
   * The amount of options that are visible without scrolling
   */
  visibleOptionsCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Give the Spinner a title to be read by screenreaders. Disables menu
   * interaction and renders a Spinner in its place.
   */
  loadingText: PropTypes.string,

  /**
   * The menu content to render when no options are present or are filtered away
   */
  emptyOption: PropTypes.node,

  /**
   * Callback fired when an option gets highlighted
   */
  onHighlightOption: PropTypes.func,

  /**
   * Callback fired on the option selection
   */
  onSelect: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
   * Options dropdown can be wider than input if maxWidth is provided
   */
  maxWidth: PropTypes.string,

  /**
   * Callback fired when the empty option is selected by click
   */
  onStaticClick: PropTypes.func,
  onBlur: PropTypes.func,
  highlightedIndex: PropTypes.number,
  expanded: PropTypes.bool,
  disabled: PropTypes.bool
}, _class2.defaultProps = {
  selectedOption: null,
  options: [],
  visibleOptionsCount: 8,
  loadingText: null,
  emptyOption: '---',
  size: 'medium',
  expanded: false,
  menuRef: function menuRef(el) {},
  onHighlightOption: function onHighlightOption(event) {},
  onSelect: function onSelect(event, selectedOption) {},
  onStaticClick: function onStaticClick(event) {},
  disabled: false,
  optionsId: void 0,
  highlightedIndex: void 0,
  maxWidth: void 0,
  onKeyDown: void 0,
  onKeyUp: void 0,
  onBlur: void 0
}, _temp)) || _class) || _class);
export default SelectOptionsList;
export { SelectOptionsList };